import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardWidgetType, AVAILABLE_WIDGETS } from '../../types/dashboard';
import InvoiceSummaryWidget from './widgets/InvoiceSummaryWidget';
import IncompleteInvoicesWidget from './widgets/IncompleteInvoicesWidget';
import IncompleteVouchersWidget from './widgets/IncompleteVouchersWidget';
import IncompleteSpecializedVouchersWidget from './widgets/IncompleteSpecializedVouchersWidget';
import TasksWidget from './widgets/TasksWidget';
import DocumentAlertsWidget from './widgets/DocumentAlertsWidget';
import WooCommerceOrdersWidget from './widgets/WooCommerceOrdersWidget';
import axios from 'axios';

const NewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userWidgets, setUserWidgets] = useState<DashboardWidgetType[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dbStatus, setDbStatus] = useState<'connected' | 'disconnected' | 'checking'>('checking');

  useEffect(() => {
    loadUserWidgets();
    checkDatabaseConnection();
    // Check database connection every 30 seconds
    const interval = setInterval(checkDatabaseConnection, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const checkDatabaseConnection = async () => {
    try {
      await axios.get('/api/system/health');
      setDbStatus('connected');
    } catch (err) {
      setDbStatus('disconnected');
    }
  };

  const loadUserWidgets = async () => {
    try {
      setLoading(true);
      // Get user's dashboard widgets from user object
      if (user?.dashboardWidgets && user.dashboardWidgets.length > 0) {
        // إذا كان مدير نظام، تأكد من أن لديه جميع الويدجات المتاحة
        if (user?.isSystemAdmin) {
          const allWidgetIds = AVAILABLE_WIDGETS.map(w => w.id);
          const currentWidgets = user.dashboardWidgets as DashboardWidgetType[];
          // إضافة أي ويدجات جديدة غير موجودة
          const missingWidgets = allWidgetIds.filter(id => !currentWidgets.includes(id));
          if (missingWidgets.length > 0) {
            setUserWidgets([...currentWidgets, ...missingWidgets]);
          } else {
            setUserWidgets(currentWidgets);
          }
        } else {
          setUserWidgets(user.dashboardWidgets as DashboardWidgetType[]);
        }
      } else {
        // Default widgets if none configured
        // مدير النظام يحصل على جميع الويدجات المتاحة
        if (user?.isSystemAdmin) {
          const allWidgetIds = AVAILABLE_WIDGETS.map(w => w.id);
          setUserWidgets(allWidgetIds);
        } else {
          setUserWidgets(['tasks']);
        }
      }
      setError(null);
    } catch (err) {
      setError('فشل في تحميل إعدادات الويدجات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleSettings = () => {
    navigate('/settings/dashboard-widgets');
  };

  const renderWidget = (widgetType: DashboardWidgetType) => {
    switch (widgetType) {
      case 'invoice_summary':
        return <InvoiceSummaryWidget key={`invoice_summary_${refreshKey}`} />;
      case 'incomplete_invoices':
        return <IncompleteInvoicesWidget key={`incomplete_invoices_${refreshKey}`} />;
      case 'incomplete_vouchers':
        return <IncompleteVouchersWidget key={`incomplete_vouchers_${refreshKey}`} />;
      case 'incomplete_specialized_vouchers':
        return <IncompleteSpecializedVouchersWidget key={`incomplete_specialized_vouchers_${refreshKey}`} />;
      case 'tasks':
        return <TasksWidget key={`tasks_${refreshKey}`} />;
      case 'document_alerts':
        return <DocumentAlertsWidget key={`document_alerts_${refreshKey}`} />;
      case 'woocommerce_orders':
        return <WooCommerceOrdersWidget key={`woocommerce_orders_${refreshKey}`} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              لوحة التحكم الرئيسية
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              مرحباً {user?.fullName}، نظرة شاملة على جميع العمليات والمهام
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                icon={dbStatus === 'connected' ? <CheckCircleIcon /> : dbStatus === 'disconnected' ? <ErrorIcon /> : <CircularProgress size={16} />}
                label={dbStatus === 'connected' ? 'قاعدة البيانات متصلة' : dbStatus === 'disconnected' ? 'قاعدة البيانات غير متصلة' : 'جاري الفحص...'}
                color={dbStatus === 'connected' ? 'success' : dbStatus === 'disconnected' ? 'error' : 'default'}
                size="small"
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              color="primary"
            >
              تحديث
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Widgets Grid */}
      {userWidgets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2, opacity: 0.5 }}>
            📊
          </Typography>
          <Typography variant="h6" sx={{ mb: 2 }}>
            لم يتم تفعيل أي ويدجات
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            يرجى الذهاب إلى إعدادات الويدجات لاختيار الويدجات التي تريد عرضها
          </Typography>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={handleSettings}
          >
            إعدادات الويدجات
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {userWidgets.map((widgetType, index) => (
            <Grid
              item
              xs={12}
              md={widgetType === 'invoice_summary' ? 12 : 6}
              lg={widgetType === 'invoice_summary' ? 12 : 4}
              key={`${widgetType}_${index}`}
            >
              {renderWidget(widgetType)}
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default NewDashboard;